import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  cn,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nexus/ui";
import { config } from "@/config";
import {
  formatBuildDiagnostic,
  hasDeployText,
  SHA_DISPLAY_LENGTH,
  toShortSha,
} from "@/features/admin/lib/build-identity";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

const PRODUCTION_ENVIRONMENT = "production";
const COARSE_TOUCH_TARGET =
  "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0";
const POPOVER_ACTION_CLASS = cn(
  "h-8 px-2",
  COARSE_TOUCH_TARGET,
  "[@media(hover:hover)_and_(pointer:fine)]:h-8",
);

interface AdminBuildIdentityProps {
  /** Opens the Releases tab. Omitted when the viewer cannot see it, or is on it. */
  onOpenReleases?: () => void;
}

/**
 * Live-build identity for the admin chrome.
 *
 * The strip only shows the environment — the one fact that must not be
 * confused. Branch, SHA, time, and releaser open on click so a crowded tab
 * list never truncates them to "m.." / "Geo...".
 */
export function AdminBuildIdentity({ onOpenReleases }: AdminBuildIdentityProps) {
  const { t } = useTranslation();
  const isProduction = config.environment === PRODUCTION_ENVIRONMENT;
  const shortSha = hasDeployText(config.deploySha) ? toShortSha(config.deploySha) : "";
  const [open, setOpen] = useState(false);
  const { copied: detailsCopied, copy: copyDetailsToClipboard } = useCopyToClipboard();

  if (!config.environment) return null;

  const hasBranch = hasDeployText(config.deployBranch);
  const hasSha = hasDeployText(config.deploySha);
  const hasReleasedAt = hasDeployText(config.deployedAt);
  const hasReleasedBy = hasDeployText(config.deployedBy);
  const hasDeployMeta = hasBranch || hasSha || hasReleasedAt || hasReleasedBy;

  const releasedLabel =
    hasReleasedAt && hasReleasedBy
      ? t("admin.header.deployedByAt", "released {{at}} by {{by}}", {
          at: config.deployedAt,
          by: config.deployedBy,
        })
      : hasReleasedAt
        ? t("admin.header.deployedAt", "released {{at}}", { at: config.deployedAt })
        : hasReleasedBy
          ? t("admin.header.deployedBy", "released by {{by}}", { by: config.deployedBy })
          : null;

  const diagnostic = formatBuildDiagnostic({
    environment: config.environment,
    branch: hasBranch ? config.deployBranch : "",
    sha: hasSha ? config.deploySha : "",
    released: releasedLabel,
  });

  const copiedLabel = t("admin.header.buildIdentity.copied", "Copied");

  return (
    <div data-testid="admin-header-meta" className="flex shrink-0 items-center gap-0.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="admin-header-build"
            className={cn(
              "inline-flex items-center gap-1 rounded-sm px-1 py-1 text-left",
              COARSE_TOUCH_TARGET,
              "[@media(hover:hover)_and_(pointer:fine)]:min-h-0",
              "transition-colors duration-150 ease-out hover:bg-muted",
              "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
            )}
            aria-label={t("admin.header.buildIdentity.open", "This build: {{env}}", {
              env: config.environment,
            })}
          >
            <Badge
              variant={isProduction ? "status-delivered" : "slate"}
              dot
              className="pointer-events-none max-w-none shrink-0"
            >
              {config.environment}
            </Badge>
            <Icon
              name="chevron-down"
              size="xs"
              color="muted"
              className="shrink-0"
              aria-hidden
            />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="w-80 p-0">
          <div className="flex flex-col gap-1 px-3 pt-3 pb-2">
            <p className="m-0 text-sm font-medium text-foreground">
              {t("admin.header.buildIdentity.title", "This build")}
            </p>
            <p className="m-0 text-xs text-muted-foreground">
              {t(
                "admin.header.buildIdentity.subtitle",
                "The app serving this session.",
              )}
            </p>
          </div>
          <dl className="flex flex-col gap-2.5 border-t border-border px-3 py-3">
            <DetailRow
              label={t("admin.header.buildIdentity.environment", "Environment")}
              value={config.environment}
            />
            {hasBranch ? (
              <DetailRow
                label={t("admin.header.buildIdentity.branch", "Branch")}
                value={config.deployBranch}
                mono
              />
            ) : null}
            {hasSha ? (
              <div className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-start gap-x-3">
                <dt className="pt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("admin.header.buildIdentity.sha", "SHA")}
                </dt>
                <dd className="m-0 min-w-0 break-all font-mono text-xs text-foreground">
                  {shortSha}
                  {config.deploySha.length > SHA_DISPLAY_LENGTH ? (
                    <span className="text-muted-foreground">
                      {config.deploySha.slice(SHA_DISPLAY_LENGTH)}
                    </span>
                  ) : null}
                </dd>
                <CopyButton
                  value={config.deploySha}
                  ariaLabel={t("admin.header.copySha", "Copy version SHA")}
                  copiedLabel={copiedLabel}
                />
              </div>
            ) : null}
            {hasReleasedAt ? (
              <DetailRow
                label={t("admin.header.buildIdentity.released", "Released")}
                value={config.deployedAt}
              />
            ) : null}
            {hasReleasedBy ? (
              <DetailRow
                label={t("admin.header.buildIdentity.releasedBy", "Released by")}
                value={config.deployedBy}
              />
            ) : null}
          </dl>
          {!hasDeployMeta ? (
            <p className="m-0 border-t border-border px-3 py-3 text-xs text-muted-foreground">
              {t(
                "admin.header.buildIdentity.local",
                "No deploy metadata on this session.",
              )}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-1 border-t border-border bg-muted/60 px-2 py-1.5">
            <Button
              variant="ghost"
              size="xs"
              className={POPOVER_ACTION_CLASS}
              onClick={() => void copyDetailsToClipboard(diagnostic)}
              iconStart={detailsCopied ? "check" : "copy"}
            >
              {detailsCopied
                ? copiedLabel
                : t("admin.header.buildIdentity.copyDetails", "Copy details")}
            </Button>
            <CopyStatus copied={detailsCopied} label={copiedLabel} />
            {onOpenReleases ? (
              <Button
                variant="ghost"
                size="xs"
                className={POPOVER_ACTION_CLASS}
                onClick={() => {
                  setOpen(false);
                  onOpenReleases();
                }}
              >
                {t("admin.header.buildIdentity.openReleases", "View on Releases")}
              </Button>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
      {hasSha ? (
        <CopyButton
          value={config.deploySha}
          ariaLabel={t("admin.header.copySha", "Copy version SHA")}
          copiedLabel={copiedLabel}
        />
      ) : null}
    </div>
  );
}

function CopyButton({
  value,
  ariaLabel,
  copiedLabel,
}: {
  value: string;
  ariaLabel: string;
  copiedLabel: string;
}) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <>
      <Button
        variant="ghost"
        size="icon-xs"
        className={cn("rounded-sm", COARSE_TOUCH_TARGET, "[@media(hover:hover)_and_(pointer:fine)]:size-5")}
        onClick={() => void copy(value)}
        aria-label={copied ? copiedLabel : ariaLabel}
      >
        <Icon
          name={copied ? "check" : "copy"}
          size="xs"
          color="inherit"
          colorVar={copied ? "--success" : undefined}
          aria-hidden
        />
      </Button>
      <CopyStatus copied={copied} label={copiedLabel} />
    </>
  );
}

function CopyStatus({ copied, label }: { copied: boolean; label: string }) {
  return (
    <span className="sr-only" role="status" aria-live="polite">
      {copied ? label : ""}
    </span>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1">
      <dt className="pt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className={cn("m-0 min-w-0 break-all text-xs text-foreground", mono && "font-mono")}>
        {value}
      </dd>
    </div>
  );
}
